import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatAuditActionFilterLabel } from "@/lib/auditActions";
import { cn } from "@/lib/utils";

interface AuditActionComboboxProps {
  /** Id of the trigger, so a `<Label htmlFor>` can address the control. */
  id?: string;
  /** The selected action key exactly as the backend emits it, or an empty string for every action. */
  value: string;
  onChange: (action: string) => void;
  /** Every action the facets endpoint returned. */
  actions: string[];
}

/**
 * Searchable picker for the audit log's Action filter.
 *
 * A plain Select stopped scaling: the facets endpoint returns every action the table holds — 71 on
 * dev (2026-09-11) — and scrolling that list for one entry was the whole complaint. Filtering is
 * local, because the list is already in memory, and matches both the raw key and its label, since an
 * operator may type either `deletion_failed` or "Deletion Failed".
 *
 * Options, the trigger and the page's filter chip all use `formatAuditActionFilterLabel`, which keeps
 * the entity the table badge drops: `resource.deleted` and `snapshot.deleted` would otherwise both
 * read "Deleted". Every option also shows its raw key under the label, the form an operator copies
 * out of an incident note.
 */
export function AuditActionCombobox({
  id,
  value,
  onChange,
  actions,
}: AuditActionComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return actions;

    return actions.filter(
      (action) =>
        action.toLowerCase().includes(term) ||
        formatAuditActionFilterLabel(action).toLowerCase().includes(term),
    );
  }, [actions, search]);

  const select = (action: string) => {
    onChange(action);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            // This trigger *is* the action field, so it follows the flat form-control contract
            // (docs/design/frontend-form-controls.md), the same as TenantCombobox: height, radius,
            // border and chrome of the SelectTrigger it replaced, not a raised outline button.
            "h-9 w-full justify-between rounded-md border-input px-3 font-normal shadow-none",
            "hover:translate-y-0 hover:border-input hover:bg-transparent",
            // "All actions" is a placeholder to the operator even though this is a <button>.
            !value && "text-placeholder",
          )}
        >
          <span className="truncate">{value ? formatAuditActionFilterLabel(value) : "All actions"}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
      >
        <div className="border-b p-2">
          <Input
            placeholder="Search actions…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
        </div>
        <div className="scrollbar-thin max-h-64 overflow-auto p-1">
          {/* Offered only while nothing is typed: a search is looking for an action, and "All
              actions" matching every term would sit on top of every result. */}
          {!search.trim() && (
            <button
              type="button"
              className={cn(
                "flex w-full cursor-pointer items-center justify-between rounded-sm px-3 py-2 text-left text-sm hover:bg-accent",
                !value && "bg-accent",
              )}
              onClick={() => select("")}
            >
              <span>All actions</span>
              {!value && <Check className="h-4 w-4 shrink-0" />}
            </button>
          )}
          {matches.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No actions found.
            </div>
          )}
          {matches.map((action) => (
            <button
              key={action}
              type="button"
              className={cn(
                "flex w-full cursor-pointer items-center justify-between gap-2 rounded-sm px-3 py-2 text-left hover:bg-accent",
                action === value && "bg-accent",
              )}
              onClick={() => select(action)}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm">{formatAuditActionFilterLabel(action)}</span>
                <span className="block truncate font-mono text-xs text-muted-foreground">
                  {action}
                </span>
              </span>
              {action === value && <Check className="h-4 w-4 shrink-0" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
